/**
 * "True Label": what a US Nutrition Facts panel can really mean (pure functions, no I/O).
 *
 * TypeScript port of com.wittdevelopment.mightypeanut.core.tef.label (FdaRounding, TrueLabelAnalyzer), checked
 * against test/tef/golden/tef-golden.json like the rest of the TEF engine.
 *
 * FDA 21 CFR 101.9(c) rounding runs both ways here: forward (true value -> printed number) and
 * inverse (printed number -> the interval of true values that could print it). The analyzer then
 * checks the printed calories against the printed macros, flags "0 calorie" servings and shows
 * the 120% ceiling FDA enforces.
 */
import { TEF_CITATIONS, TefInputError, type Citation } from './engine';

// ---------------------------------------------------------------------
// Intervals and declared values
// ---------------------------------------------------------------------
/** Half-open interval [low, high) of true values that could print as a given label number. */
export interface ValueInterval {
  readonly low: number;
  readonly high: number;
}

export function valueInterval(low: number, high: number): ValueInterval {
  if (!(low <= high)) throw new TefInputError(`Expected low <= high, got ${low} / ${high}`);
  return { low, high };
}

export const intervalWidth = (v: ValueInterval): number => v.high - v.low;
export const intervalMid = (v: ValueInterval): number => (v.low + v.high) / 2.0;
export const intervalContains = (v: ValueInterval, x: number): boolean => x >= v.low && x < v.high;
export const intervalsOverlap = (a: ValueInterval, b: ValueInterval): boolean => a.low < b.high && b.low < a.high;
export const scaleInterval = (v: ValueInterval, k: number): ValueInterval => valueInterval(v.low * k, v.high * k);
export const addIntervals = (a: ValueInterval, b: ValueInterval): ValueInterval => valueInterval(a.low + b.low, a.high + b.high);

/** FDA's "less than 1 g" wording. */
export const LESS_THAN_ONE = '<1' as const;
/** What a label printed for a gram field: a number, or "less than 1 g". */
export type DeclaredGrams = number | typeof LESS_THAN_ONE;

function requireNonNegative(x: number, what: string): void {
  if (!(x >= 0)) throw new TefInputError(`${what} must be non-negative`);
}

// ---------------------------------------------------------------------
// FDA rounding (21 CFR 101.9(c)); round-half-up at every boundary
// ---------------------------------------------------------------------
const roundHalfUp = (x: number, step: number): number => Math.floor(x / step + 0.5) * step;

/** Calories: under 5 -> 0; up to 50 -> nearest 5; above 50 -> nearest 10. */
export function roundCalories(trueKcal: number): number {
  if (trueKcal < 5.0) return 0.0;
  if (trueKcal <= 50.0) return roundHalfUp(trueKcal, 5.0);
  return roundHalfUp(trueKcal, 10.0);
}

export function caloriesInterval(declared: number): ValueInterval {
  requireNonNegative(declared, 'Declared calories');
  if (declared === 0.0) return valueInterval(0.0, 5.0);
  // Off the rounding grid (labels print anything under 5 as 0); keep the interval valid instead of inverting it.
  if (declared < 5.0) return valueInterval(Math.max(0.0, declared - 2.5), declared + 2.5);
  if (declared < 50.0) return valueInterval(Math.max(5.0, declared - 2.5), declared + 2.5);
  // 50 can come from 47.5..50 (nearest 5) or 50..55 (nearest-10 zone rounding down).
  if (declared === 50.0) return valueInterval(47.5, 55.0);
  // Values in (50, 55) print as 50, so nothing above 50 can start below 55.
  return valueInterval(Math.max(55.0, declared - 5.0), declared + 5.0);
}

/** Total, saturated and trans fat: under 0.5 -> 0; under 5 -> nearest 0.5; 5 and up -> nearest 1. */
export function roundFat(trueG: number): number {
  if (trueG < 0.5) return 0.0;
  if (trueG < 5.0) return roundHalfUp(trueG, 0.5);
  return roundHalfUp(trueG, 1.0);
}

export function fatInterval(declared: number): ValueInterval {
  requireNonNegative(declared, 'Declared fat');
  if (declared === 0.0) return valueInterval(0.0, 0.5);
  // Off the rounding grid (under 0.5 g prints as 0).
  if (declared < 0.5) return valueInterval(Math.max(0.0, declared - 0.25), declared + 0.25);
  if (declared < 5.0) return valueInterval(Math.max(0.5, declared - 0.25), declared + 0.25);
  if (declared === 5.0) return valueInterval(4.75, 5.5);
  return valueInterval(declared - 0.5, declared + 0.5);
}

/** Carbohydrate, fiber, sugars, protein, sugar alcohols: under 0.5 -> 0; 0.5 to under 1 -> "<1"; else nearest gram. */
export function roundGram(trueG: number): DeclaredGrams {
  if (trueG < 0.5) return 0.0;
  if (trueG < 1.0) return LESS_THAN_ONE;
  return roundHalfUp(trueG, 1.0);
}

export function gramInterval(declared: DeclaredGrams): ValueInterval {
  // FDA also allows "<1 g" for values under 0.5.
  if (declared === LESS_THAN_ONE) return valueInterval(0.0, 1.0);
  requireNonNegative(declared, 'Declared grams');
  if (declared === 0.0) return valueInterval(0.0, 0.5);
  // 0.5 to 1 may be printed as "1 g" instead of "<1 g".
  if (declared === 1.0) return valueInterval(0.5, 1.5);
  // Values under 1 other than 0 are off the grid; never let the lower bound go negative.
  return valueInterval(Math.max(0.0, declared - 0.5), declared + 0.5);
}

// ---------------------------------------------------------------------
// Label analysis
// ---------------------------------------------------------------------
/** Exactly what is printed on a US Nutrition Facts panel for one serving. */
export interface DeclaredLabel {
  caloriesKcal: number;
  fatG: number;
  totalCarbG: DeclaredGrams;
  proteinG: DeclaredGrams;
  fiberG?: DeclaredGrams | null;
  sugarAlcoholG?: DeclaredGrams | null;
  servingSizeG?: number | null;
  servingsPerContainer?: number | null;
}

export type LabelVerdict = 'CONSISTENT' | 'CONSISTENT_WITH_FIBER_ADJUSTMENT' | 'LABEL_BELOW_MACROS' | 'LABEL_ABOVE_MACROS' | 'BETWEEN_METHODS';

export const LABEL_VERDICT_HEADLINE: Readonly<Record<LabelVerdict, string>> = {
  CONSISTENT: 'Label is consistent with its own macros',
  CONSISTENT_WITH_FIBER_ADJUSTMENT: 'Consistent once fiber/sugar alcohols are discounted (FDA method C)',
  LABEL_BELOW_MACROS: 'Printed calories are LOWER than the macros allow — beyond rounding',
  LABEL_ABOVE_MACROS: 'Printed calories are HIGHER than the macros allow — beyond rounding',
  BETWEEN_METHODS: 'Between the two FDA methods: fiber or sugar alcohols counted at part value',
};

export interface TrueLabelReport {
  declared: DeclaredLabel;
  caloriesInterval: ValueInterval;
  fatInterval: ValueInterval;
  carbInterval: ValueInterval;
  proteinInterval: ValueInterval;
  fiberInterval: ValueInterval | null;
  sugarAlcoholInterval: ValueInterval | null;
  /** 4-4-9 on total carbohydrate (FDA method B), over every true value the rounded macros allow. */
  methodBKcal: ValueInterval;
  /** Fiber 0-2 kcal/g, polyols 0-3 kcal/g, rest 4-4-9 (FDA method C). */
  methodCKcal: ValueInterval;
  verdict: LabelVerdict;
  /** Size of the unexplained gap in kcal per serving (0 if consistent). */
  unexplainedGapKcal: number;
  /** FDA only fails a product if true calories exceed 120% of the declared value. */
  legalCeilingKcal: number;
  /** Max kcal a "0 calorie" serving could still contain; null unless calories are declared 0. */
  zeroCalorieHiddenMaxPerServing: number | null;
  zeroCalorieHiddenMaxPerContainer: number | null;
  /**
   * Best single guess from the macros alone: centre of method B, of method C when C explains the label,
   * or of the printed range when the label sits between the two methods.
   */
  macroBestEstimateKcal: number;
  findings: string[];
  citations: Citation[];
}

const MAX_FIBER_KCAL_PER_G = 2.0; // FDA: soluble 2, insoluble 0
const MAX_POLYOL_KCAL_PER_G = 3.0; // FDA: HSH 3.0 highest, erythritol 0
const FDA_CLASS_II_CEILING = 1.20; // 21 CFR 101.9(g)(5)

export function analyzeLabel(label: DeclaredLabel): TrueLabelReport {
  const kcal: ValueInterval = caloriesInterval(label.caloriesKcal);
  const fat: ValueInterval = fatInterval(label.fatG);
  const carb: ValueInterval = gramInterval(label.totalCarbG);
  const protein: ValueInterval = gramInterval(label.proteinG);
  const fiber: ValueInterval | null = label.fiberG === null || label.fiberG === undefined ? null : gramInterval(label.fiberG);
  const polyol: ValueInterval | null = label.sugarAlcoholG === null || label.sugarAlcoholG === undefined ? null : gramInterval(label.sugarAlcoholG);

  const methodB: ValueInterval = addIntervals(addIntervals(scaleInterval(protein, 4.0), scaleInterval(carb, 4.0)), scaleInterval(fat, 9.0));

  const fiberLow: number = fiber?.low ?? 0.0;
  const fiberHigh: number = fiber?.high ?? 0.0;
  const polyLow: number = polyol?.low ?? 0.0;
  const polyHigh: number = polyol?.high ?? 0.0;
  const availLow: number = Math.max(carb.low - fiberHigh - polyHigh, 0.0);
  const availHigh: number = Math.max(carb.high - fiberLow - polyLow, 0.0);
  const methodC: ValueInterval = valueInterval(
    protein.low * 4 + availLow * 4 + fat.low * 9, // fiber and polyols at 0 kcal/g
    protein.high * 4 + availHigh * 4 + fat.high * 9 + fiberHigh * MAX_FIBER_KCAL_PER_G + polyHigh * MAX_POLYOL_KCAL_PER_G,
  );

  let verdict: LabelVerdict;
  let gap: number;
  if (intervalsOverlap(kcal, methodB)) {
    verdict = 'CONSISTENT'; gap = 0.0;
  } else if (intervalsOverlap(kcal, methodC)) {
    verdict = 'CONSISTENT_WITH_FIBER_ADJUSTMENT'; gap = 0.0;
  } else if (kcal.high <= Math.min(methodB.low, methodC.low)) {
    verdict = 'LABEL_BELOW_MACROS'; gap = Math.min(methodB.low, methodC.low) - kcal.high;
  } else if (kcal.low >= Math.max(methodB.high, methodC.high)) {
    verdict = 'LABEL_ABOVE_MACROS'; gap = kcal.low - Math.max(methodB.high, methodC.high);
  } else {
    // Method C never starts above method B, so what is left is a label strictly between method C's
    // maximum and method B's minimum: a partial discount, not hidden calories.
    verdict = 'BETWEEN_METHODS'; gap = 0.0;
  }

  const zeroMax: number | null = label.caloriesKcal === 0.0 ? Math.min(kcal.high, methodB.high) : null;
  const perContainer: number | null = label.servingsPerContainer ?? null;
  const zeroMaxContainer: number | null = zeroMax === null || perContainer === null ? null : zeroMax * perContainer;
  const legalCeiling: number = label.caloriesKcal * FDA_CLASS_II_CEILING;

  const findings: string[] = [];
  findings.push(`Printed ${formatKcal(label.caloriesKcal)} kcal means the true value is somewhere in ${formatKcal(kcal.low)}–${formatKcal(kcal.high)} kcal (FDA rounding).`);
  findings.push(`The printed macros (after their own rounding) allow ${formatKcal(methodB.low)}–${formatKcal(methodB.high)} kcal by 4-4-9.`);
  switch (verdict) {
    case 'CONSISTENT':
      findings.push('No evidence of anything hidden beyond normal rounding.');
      break;
    case 'CONSISTENT_WITH_FIBER_ADJUSTMENT':
      findings.push('Gap is explained by the manufacturer discounting fiber / sugar alcohols — a legal method.');
      break;
    case 'LABEL_BELOW_MACROS':
      findings.push(
        `At least ${formatKcal(gap)} kcal/serving can't be explained by rounding. Legal explanations: ` +
          'food-specific Atwater factors or bomb calorimetry (methods A, D, E), or unlisted fiber. ' +
          "Otherwise it's a label error.",
      );
      break;
    case 'LABEL_ABOVE_MACROS':
      findings.push(
        `Label claims ${formatKcal(gap)}+ kcal more than the macros support. Possibly unlisted alcohol, ` +
          'a specific-factor method, or a label error.',
      );
      break;
    case 'BETWEEN_METHODS':
      findings.push(
        `The printed calories fall between the fiber-adjusted maximum (${formatKcal(methodC.high)} kcal) and the ` +
          `4-4-9 minimum (${formatKcal(methodB.low)} kcal): fiber or sugar alcohols were counted at part value, ` +
          'or food-specific factors were used (methods A, D, E). Nothing hidden beyond that.',
      );
      break;
  }
  if (zeroMax !== null && zeroMax > 0.0) {
    findings.push(
      `"0 calories" can legally mean up to ${formatKcal(zeroMax)} kcal per serving` +
        (zeroMaxContainer === null ? '' : ` — up to ${formatKcal(zeroMaxContainer)} kcal for the whole container`) + '.',
    );
  }
  if (label.fatG === 0.0) findings.push('"0 g fat" can mean up to 0.49 g (≈4.4 kcal) per serving.');
  findings.push(
    'Separately from rounding, FDA only acts if real calories exceed 120% of the label ' +
      `(here: ${formatKcal(legalCeiling)} kcal). Lab studies found US packaged ` +
      'foods run a median ~4–8% above label.',
  );

  return {
    declared: label,
    caloriesInterval: kcal, fatInterval: fat, carbInterval: carb, proteinInterval: protein,
    fiberInterval: fiber, sugarAlcoholInterval: polyol,
    methodBKcal: methodB, methodCKcal: methodC,
    verdict, unexplainedGapKcal: gap,
    legalCeilingKcal: legalCeiling,
    zeroCalorieHiddenMaxPerServing: zeroMax,
    zeroCalorieHiddenMaxPerContainer: zeroMaxContainer,
    macroBestEstimateKcal: verdict === 'CONSISTENT_WITH_FIBER_ADJUSTMENT' ? intervalMid(methodC) : verdict === 'BETWEEN_METHODS' ? intervalMid(kcal) : intervalMid(methodB),
    findings,
    citations: [TEF_CITATIONS.CFR_101_9, TEF_CITATIONS.JUMPERTZ_2013, TEF_CITATIONS.URBAN_2010],
  };
}

/**
 * Whole numbers print without decimals; anything else to one decimal, rounding half up on the
 * shortest decimal form of the number (Java's "%.1f"), so 0.15 -> "0.2" where toFixed gives "0.1".
 */
export function formatKcal(x: number): string {
  if (x === Math.floor(x)) return Math.trunc(x).toString();
  const negative: boolean = x < 0;
  const [intDigits, fracDigits] = shortestDecimal(Math.abs(x));
  // Round half up at one decimal place, working on the digit string.
  const firstFrac: number = Number(fracDigits[0] ?? '0');
  const roundUp: boolean = Number(fracDigits[1] ?? '0') >= 5;
  let scaled: bigint = BigInt(intDigits) * 10n + BigInt(firstFrac) + (roundUp ? 1n : 0n);
  const sign: string = negative && scaled !== 0n ? '-' : '';
  if (scaled < 0n) scaled = -scaled;
  const s: string = scaled.toString().padStart(2, '0');
  return `${sign}${s.slice(0, -1)}.${s.slice(-1)}`;
}

/** Integer and fraction digit strings of the shortest round-trip decimal for a non-negative finite number. */
function shortestDecimal(x: number): [string, string] {
  const s: string = x.toString();
  const m: RegExpMatchArray | null = /^(\d+)(?:\.(\d+))?(?:e([+-]\d+))?$/.exec(s);
  if (!m) throw new TefInputError(`Cannot format ${s}`);
  const whole: string = m[1] ?? '0';
  const frac: string = m[2] ?? '';
  const exp: number = m[3] ? Number(m[3]) : 0;
  const digits: string = whole + frac;
  const point: number = whole.length + exp;
  if (point <= 0) return ['0', '0'.repeat(-point) + digits];
  if (point >= digits.length) return [digits + '0'.repeat(point - digits.length), ''];
  return [digits.slice(0, point), digits.slice(point)];
}
